import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "剧情专题", to: "/category" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/category") return location.pathname === "/category";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "bg-background-50/95 border-b border-background-200/70 backdrop-blur-md"
          : "bg-gradient-to-b from-background-50/80 to-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer group">
          <span className="relative w-10 h-10 rounded-md bg-primary-500 flex items-center justify-center overflow-hidden">
            <i className="ri-train-fill text-foreground-950 text-xl" />
            <span className="absolute inset-x-0 bottom-0 h-[3px] bg-accent-500" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl md:text-2xl font-semibold tracking-wide text-foreground-950">
              痴汉电车
            </span>
            <span className="font-label text-[10px] tracking-[0.3em] text-foreground-500 mt-1">
              CINEMA ARCHIVE
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`relative px-3 lg:px-4 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                isActive(item.to)
                  ? "text-foreground-950"
                  : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100/70"
              }`}
            >
              {item.label}
              {isActive(item.to) && (
                <span className="absolute left-3 right-3 -bottom-[1px] h-[2px] bg-primary-500 rounded-full" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2 bg-background-100/80 border border-background-200/80 rounded-md pl-3 pr-1 py-1 w-56 xl:w-64">
            <i className="ri-search-line text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索作品 / 剧情关键词"
              className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none py-1"
              aria-label="站内搜索"
            />
            <button
              type="submit"
              className="shrink-0 w-7 h-7 rounded bg-primary-500 hover:bg-primary-600 text-foreground-950 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="提交搜索"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          <Link
            to="/search"
            className="hidden md:flex lg:hidden w-10 h-10 rounded-md border border-background-200/80 text-foreground-800 items-center justify-center cursor-pointer hover:border-primary-500 transition-colors"
            aria-label="前往搜索页"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="md:hidden w-10 h-10 rounded-md border border-background-200/80 text-foreground-900 flex items-center justify-center cursor-pointer"
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 animate-slide-down">
          <div className="px-4 py-4 flex flex-col gap-1">
            <form onSubmit={submitSearch} className="flex items-center gap-2 bg-background-100 border border-background-200/80 rounded-md px-3 py-2 mb-3">
              <i className="ri-search-line text-foreground-500 text-sm" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品 / 剧情关键词"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
                aria-label="站内搜索"
              />
              <button type="submit" className="text-primary-400 text-sm px-1 cursor-pointer" aria-label="提交搜索">
                搜索
              </button>
            </form>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between px-3 py-3 rounded-md text-sm ${
                  isActive(item.to) ? "bg-background-100 text-foreground-950" : "text-foreground-700"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}